/**
 * Settings Store - User preferences and configuration
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { PlcBrand, PlcLanguage } from '@realplc/types';
import { scopedStorage } from '../utils/scopedSettingsStorage';

interface SettingsStore {
  // Theme
  theme: 'dark' | 'light';
  setTheme: (theme: 'dark' | 'light') => void;

  // PLC Settings
  plcBrand: PlcBrand;
  plcLanguage: PlcLanguage;
  setPlcBrand: (brand: PlcBrand) => void;
  setPlcLanguage: (language: PlcLanguage) => void;

  // AI Settings
  routingMode: string;
  preferredProvider: string;
  preferredModel: string;
  setRoutingMode: (mode: string) => void;
  setPreferredProvider: (provider: string) => void;
  setPreferredModel: (model: string) => void;

  // UI Settings
  uiLanguage: 'English' | 'Chinese';
  setUiLanguage: (lang: 'English' | 'Chinese') => void;
  showMermaidDiagram: boolean;
  setShowMermaidDiagram: (show: boolean) => void;

  // Plan Approval
  planAutoApproval: { enabled: boolean; seconds: number };
  setPlanAutoApproval: (config: { enabled: boolean; seconds: number }) => void;

  // Reset
  resetSettings: () => void;
}

const DEFAULT_SETTINGS = {
  theme: 'dark' as const,
  plcBrand: PlcBrand.Codesys,
  plcLanguage: PlcLanguage.ST,
  routingMode: 'fast',
  preferredProvider: 'autofree',
  preferredModel: '',
  uiLanguage: 'English' as const,
  showMermaidDiagram: true,
  planAutoApproval: { enabled: false, seconds: 5 },
};

export const useSettingsStore = create<SettingsStore>()(
  persist(
    (set) => ({
      ...DEFAULT_SETTINGS,

      setTheme: (theme) => set({ theme }),
      setPlcBrand: (plcBrand) => set({ plcBrand }),
      setPlcLanguage: (plcLanguage) => set({ plcLanguage }),
      setRoutingMode: (routingMode) => set({ routingMode }),
      setPreferredProvider: (preferredProvider) => set({ preferredProvider }),
      setPreferredModel: (preferredModel) => set({ preferredModel }),
      setUiLanguage: (uiLanguage) => set({ uiLanguage }),
      setShowMermaidDiagram: (showMermaidDiagram) => set({ showMermaidDiagram }),
      setPlanAutoApproval: (planAutoApproval) => set({ planAutoApproval }),

      resetSettings: () => set(DEFAULT_SETTINGS),
    }),
    {
      name: 'realplc-settings',
      storage: createJSONStorage(() => scopedStorage),
      skipHydration: true,
    }
  )
);
